import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchInbox,
  markNotificationRead,
  deleteNotification,
} from '../api/notifications';
import type { InboxQueryParams } from '../types';

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: (params: Omit<InboxQueryParams, 'cursor'>) =>
    [...notificationsKeys.all, 'list', params] as const,
};

const DEFAULT_LIMIT = 20;

export function useNotifications(params: Omit<InboxQueryParams, 'cursor'> = {}) {
  return useInfiniteQuery({
    queryKey: notificationsKeys.list(params),
    queryFn: ({ pageParam }) =>
      fetchInbox({
        ...params,
        limit: params.limit ?? DEFAULT_LIMIT,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useMarkNotificationRead() {
  return useInboxMutation(markNotificationRead);
}

export function useDeleteNotification() {
  return useInboxMutation(deleteNotification);
}

// Every inbox action targets one notification and leaves the feed stale, so they all invalidate alike
function useInboxMutation(mutationFn: (notificationId: string) => Promise<void>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}
